import { type ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAssociate from '@/icons/Associate.svg';
import IconCloth from '@/icons/Cloth.svg';
import IconEdit from '@/icons/Edit.svg';
import IconUsers from '@/icons/Users.svg';
import IconWorkshop from '@/icons/Workshop.svg';
import { AgreementFromGetById, AMOUNT_TO_CONSIDER } from '@/models/agreements';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { formatPercentage } from '@/utils/number';

import { PriceCatalogAgreementTables } from './components/PriceAgreementTable';
import { AgreementTypeInfo } from './AgreementTypeInfo';

export const AgreementRead = ({
  agreement,
  onEdit,
}: {
  agreement: AgreementFromGetById;
  onEdit: () => void;
}) => (
  <main>
    <Stack gap="0.5rem">
      <Stack
        row
        className="settings-section__record-header"
        gap="1rem"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack row gap="0.5rem" alignItems="center">
          <SettingsBreadcrumbParent
            to="/settings_beta/network/agreements"
            label={
              <Trans id="settings.organization.entities.agreements.detail.back">Agreements</Trans>
            }
          />
          <Title as="h1" size="200" weight="medium">
            <Trans id="settings.organization.entities.agreements.detail.title">Agreement</Trans>
          </Title>
        </Stack>
        <Button iconOnly variant="secondary" size="medium" onPress={onEdit}>
          <IconEdit />
        </Button>
      </Stack>
      <AgreementTypeInfo type={agreement.type} />
      <Box padding="0">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            padding: '1rem',
          }}
        >
          <AgreementSideList
            label={
              <Trans id="settings.organization.entities.agreements.detail.left-side">
                Between ...
              </Trans>
            }
            entities={agreement.entitiesA}
            groups={agreement.entityGroupsA}
            hasClient={agreement.clientA}
          />
          <AgreementSideList
            label={
              <Trans id="settings.organization.entities.agreements.detail.right-side">
                and ...
              </Trans>
            }
            entities={agreement.entitiesB}
            groups={agreement.entityGroupsB}
            hasClient={agreement.clientB}
          />
        </div>

        <AgreementAppliesWhen agreement={agreement} />
      </Box>

      {agreement.type === 'price_catalog' && <PriceCatalogAgreement agreement={agreement} />}
      {agreement.type === 'transit' && <TransitAgreement agreement={agreement} />}
      {agreement.type === 'after_sales' && <AfterSalesAgreement agreement={agreement} />}
    </Stack>
  </main>
);

const AgreementSideList = ({
  label,
  entities,
  groups,
  hasClient,
}: {
  label: ReactNode;
  entities: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  hasClient: boolean;
}) => {
  const hasEntitiesOrGroups = entities.length + groups.length > 0;

  return (
    <Stack gap="0.25rem">
      <Title color="secondary" weight="medium">
        {label}
      </Title>
      {hasEntitiesOrGroups || hasClient ? (
        <Stack gap="0.25rem" alignItems="start" flexWrap="nowrap">
          {groups.map((group) => (
            <Stack key={group.id} row gap="0.25rem" alignItems="center" flexWrap="nowrap">
              <IconAssociate
                style={{ fontSize: '1.25rem', color: 'var(--color-text-secondary-black)' }}
              />
              <Button variant="link-subtle" to={`/settings_beta/network/groups/${group.id}`}>
                <InlineText size="50" weight="medium">
                  {group.name}
                </InlineText>
              </Button>
            </Stack>
          ))}
          {entities.map((entity) => (
            <Stack key={entity.id} row gap="0.25rem" alignItems="center" flexWrap="nowrap">
              <IconAssociate
                style={{ fontSize: '1.25rem', color: 'var(--color-text-secondary-black)' }}
              />
              <Button variant="link-subtle" to={`/settings_beta/network/entities/${entity.id}`}>
                <InlineText size="50" weight="medium">
                  {entity.name}
                </InlineText>
              </Button>
            </Stack>
          ))}
          {hasClient && (
            <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
              <IconUsers
                style={{ fontSize: '1.25rem', color: 'var(--color-text-secondary-black)' }}
              />
              <Paragraph size="50" weight="medium">
                <Trans id="settings.organization.entities.agreements.table.clients">Clients</Trans>
              </Paragraph>
            </Stack>
          )}
        </Stack>
      ) : (
        <Paragraph color="secondary">—</Paragraph>
      )}
    </Stack>
  );
};

const AgreementAppliesWhen = ({ agreement }: { agreement: AgreementFromGetById }) => {
  if (agreement.articleWorkshops.length === 0 && agreement.articleBrands.length === 0) {
    return null;
  }

  return (
    <>
      <hr />
      <Stack gap="0.25rem" padding="1rem">
        {agreement.articleBrands.length > 0 && (
          <>
            <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
              <IconCloth style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
              <Paragraph>
                <Trans id="settings.organization.entities.agreements.detail.applies-when.brands">
                  This agreement applies to the following brands:
                </Trans>
              </Paragraph>
            </Stack>
            <Stack gap="0.25rem" alignItems="start" style={{ paddingLeft: '1.25rem' }}>
              {agreement.articleBrands.map(({ id, name }) => (
                <Button key={id} variant="link-subtle" to={`/settings_beta/catalog/brands/${id}`}>
                  {name}
                </Button>
              ))}
            </Stack>
          </>
        )}
        {agreement.articleWorkshops.length > 0 && (
          <>
            <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
              <IconWorkshop
                style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }}
              />
              <Paragraph>
                <Trans id="settings.organization.entities.agreements.detail.applies-when.workshops">
                  This agreement applies when the assigned workshop is one of the following:
                </Trans>
              </Paragraph>
            </Stack>
            <Stack gap="0.25rem" alignItems="start" style={{ paddingLeft: '1.25rem' }}>
              {agreement.articleWorkshops.map(({ id, name }) => (
                <Button key={id} variant="link-subtle" to={`/settings_beta/network/entities/${id}`}>
                  {name}
                </Button>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </>
  );
};

const PriceCatalogAgreement = ({ agreement }: { agreement: AgreementFromGetById }) => {
  const { i18n } = useLingui();

  return (
    <>
      <Box gap="0.25rem">
        {agreement.clientB && (
          <Paragraph>
            <Trans id="settings.organization.entities.agreements.detail.default-margin">
              <InlineText weight="medium">Default margin:</InlineText>{' '}
              {agreement.margin ? formatPercentage(agreement.margin) : 'None'}
            </Trans>
          </Paragraph>
        )}
        <Paragraph>
          <Trans id="settings.organization.entities.agreements.detail.default-custom-actions-tax-rates">
            <InlineText weight="medium">Default custom actions tax rates:</InlineText>{' '}
            {agreement.currency ? (
              <>
                {agreement.defaultCustomPriceTaxRates.map(formatPercentage).join(', ') ||
                  formatPercentage(0)}{' '}
                ({agreement.currency})
              </>
            ) : (
              'None'
            )}
          </Trans>
        </Paragraph>
        <Paragraph>
          <InlineText weight="medium">
            <Trans id="settings.organization.entities.agreements.detail.amount-to-consider.read-label">
              Price defined from:
            </Trans>
          </InlineText>{' '}
          {i18n._(AMOUNT_TO_CONSIDER[agreement.amountToConsider])}
        </Paragraph>
      </Box>
      <PriceCatalogAgreementTables agreement={agreement} />
    </>
  );
};

const TransitAgreement = ({ agreement }: { agreement: AgreementFromGetById }) => {
  return (
    <Box gap="0.25rem">
      <Paragraph>
        <Trans id="settings.organization.entities.agreements.detail.self-handled-transit-enabled">
          <InlineText weight="medium">Self-handled transit enabled:</InlineText>{' '}
          {agreement.selfHandledTransitEnabled ? 'Yes' : 'No'}
        </Trans>
      </Paragraph>
    </Box>
  );
};

const AfterSalesAgreement = ({ agreement }: { agreement: AgreementFromGetById }) => {
  if (!agreement.clientB) {
    return null;
  }

  return (
    <Box gap="0.25rem">
      <Paragraph>
        <InlineText weight="medium">
          <Trans id="settings.organization.entities.agreements.detail.supported-countries.read-label">
            Client countries:
          </Trans>
        </InlineText>{' '}
        {agreement.supportedCountries.length > 0 ? agreement.supportedCountries.join(', ') : '—'}
      </Paragraph>
      <Paragraph color="secondary" size="100">
        <Trans id="settings.organization.entities.agreements.detail.supported-countries.description">
          Only applies to web-client requests (with no store).
        </Trans>
      </Paragraph>
    </Box>
  );
};
